import React, { useMemo, useState } from 'react';

import { DISCHARGE_TYPE_LABELS, duesAgingDays } from '../../../constants/admission';
import { formatPaise } from '../../../utils/money';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import {
  writeOffReasonState,
  writeOffPartialContextLine,
  writeOffErrorMessage,
  writeOffSuccessMessage,
  writeOffConfirmCopy,
} from './writeOffDuesModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const textareaCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm min-h-[72px]';

/**
 * WriteOffDuesDialog (C6b · S4, FE-5) — "Write off dues", the admin-only
 * cousin of the C6b Collect-dues dialog: books the ENTIRE remaining due as
 * uncollectable. Deliberately does NOT look like a money dialog — no amount
 * field (server-computed remainder only), no mode/reference, no receipt; an
 * amber (caution) primary button that always restates the exact amount, and
 * a mandatory second-step `ConfirmationModal` (copy-deck §6) before it fires
 * (money dialogs get away with one step; a terminal, unreversible loss entry
 * gets two).
 *
 * The SERVER's `admission.dues_paise` is the single source for the amount
 * being written off — this dialog never types/computes it. `onSubmit(body)`
 * calls `writeOffDues` (the mounter binds the admission id); NO idempotency
 * key is minted (R-3 — the backend's singleton write-off record row is its
 * own replay guard).
 *
 * Typed errors (DUES_CHANGED / DUES_NOT_OPEN / REASON_REQUIRED) map to
 * ratified copy via `writeOffDuesModel.writeOffErrorMessage`; a DUES_CHANGED
 * response also refreshes the LOCAL "effective outstanding" from the
 * server's own fresh `dues_paise` extra (still single-source, never a client
 * re-derive) so the loser of a collect-vs-write-off race sees the true
 * figure before retrying.
 *
 * @param admission    { admission_id, patient_name, ip_number, discharge_type,
 *                       discharged_at, dues_paise, dues_status,
 *                       dues_collected_paise? } — the last field is ABSENT on
 *                       a queue-sourced row (contract §2 doesn't carry it);
 *                       the part-collected context line simply doesn't render
 *                       in that case (ASSUMPTION — see integration-notes.md).
 * @param canWriteOff  bool — WRITE_OFF_DUES perm; false renders the
 *                     defensive "Only an admin can write off dues." state
 *                     instead of the form (the mounter also gates the whole
 *                     render branch on this perm — belt-and-braces per the
 *                     copy-deck's own "non-admin reaching the dialog" line).
 * @param onClose
 * @param onSubmit(body) → { success, data?, error?, code?, ...extras } (calls writeOffDues)
 */
export default function WriteOffDuesDialog({ admission, canWriteOff = true, onClose, onSubmit }) {
  // Effective outstanding — starts from the server-cached prop; only ever
  // overwritten by a DUES_CHANGED response's OWN fresh `dues_paise` (still a
  // server-written value, never a client re-derive).
  const [effectiveDuesPaise, setEffectiveDuesPaise] = useState(Math.max(0, Math.round(Number(admission.dues_paise) || 0)));
  const [duesChangedNotice, setDuesChangedNotice] = useState(false);

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // success envelope
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fieldState = useMemo(() => writeOffReasonState(reason), [reason]);
  const partialLine = useMemo(
    () => writeOffPartialContextLine({ duesPaise: effectiveDuesPaise, duesCollectedPaise: admission.dues_collected_paise }),
    [effectiveDuesPaise, admission.dues_collected_paise],
  );
  const agingDays = duesAgingDays(admission.discharged_at);
  const patientName = admission.patient_name || admission.patient_id;

  const subtitle = `${patientName} · IP no. ${admission.ip_number}`
    + `${admission.discharge_type ? ` · ${DISCHARGE_TYPE_LABELS[admission.discharge_type] || admission.discharge_type}` : ''}`
    + `${admission.discharged_at ? ` · discharged ${new Date(admission.discharged_at).toLocaleDateString()}` : ''}`;

  const requestConfirm = () => {
    if (!fieldState.valid || busy) return;
    setConfirmOpen(true);
  };

  const submit = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    setDuesChangedNotice(false);
    const res = await onSubmit({
      reason: reason.trim(),
      expected_dues_paise: effectiveDuesPaise,
    });
    setBusy(false);
    if (res && res.success) {
      setDone(res.data);
      return;
    }
    if (res) {
      if (res.code === 'DUES_CHANGED' && res.dues_paise != null) {
        setEffectiveDuesPaise(Math.max(0, Math.round(Number(res.dues_paise) || 0)));
        setDuesChangedNotice(true);
      }
      const mapped = writeOffErrorMessage(res.code, res);
      setError(mapped || res.error || 'Could not write off the dues. Check the values and try again.');
      // values retained — dialog stays open
    }
  };

  // ── Defensive: non-admin reached the dialog anyway ──────────────────────
  if (!canWriteOff) {
    return (
      <div className={overlayCls} data-testid="writeoff-dues-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Write off dues</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4" data-testid="writeoff-dues-non-admin">
            <p className="text-[13px] text-slate-700 dark:text-slate-200">Only an admin can write off dues.</p>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="writeoff-dues-close">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Nothing left to write off (opened on an already-closed due) ────────
  if (effectiveDuesPaise <= 0 && !done) {
    return (
      <div className={overlayCls} data-testid="writeoff-dues-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Write off dues</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4" data-testid="writeoff-dues-already-closed">
            <p className="text-[13px] text-slate-700 dark:text-slate-200">This due is already closed. Nothing remains to write off.</p>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="writeoff-dues-close">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Success (neutral — no celebratory tone, no print prompt) ───────────
  if (done) {
    return (
      <div className={overlayCls} data-testid="writeoff-dues-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Write off dues</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4" data-testid="writeoff-dues-success">
            <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[13px] p-3">
              {writeOffSuccessMessage(done)}
            </div>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button type="button" className={btnSecondary} onClick={onClose} data-testid="writeoff-dues-done">Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Write-off form ───────────────────────────────────────────────────────
  return (
    <div className={overlayCls} data-testid="writeoff-dues-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Write off dues</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            {subtitle}
          </div>

          {duesChangedNotice && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="writeoff-dues-changed-notice">
              The amount due has been updated to {formatPaise(effectiveDuesPaise)}.
            </div>
          )}

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5" data-testid="writeoff-dues-amount">
            <span className="text-[13px] text-slate-600 dark:text-slate-300">Amount to write off: </span>
            <b className="text-[15px] tabular-nums text-slate-900 dark:text-white">{formatPaise(effectiveDuesPaise)} (the full remaining due)</b>
          </div>

          {partialLine && (
            <div className="text-[12px] text-slate-500 dark:text-slate-400" data-testid="writeoff-dues-partial-context">{partialLine}</div>
          )}

          <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 text-[12.5px] p-3" data-testid="writeoff-dues-explanation">
            {`This records ${formatPaise(effectiveDuesPaise)} as uncollectable — a loss to the organisation. It is not a payment and not a discount: no money is received and no receipt is issued. The remaining due becomes ₹0 and this admission leaves Payment Pending.`}
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="writeoff-dues-error">{error}</div>
          )}

          <div>
            <label className={labelCls} htmlFor="wo-reason">Reason (required)</label>
            <textarea id="wo-reason" className={textareaCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="writeoff-dues-reason" />
            <p className="text-[11px] mt-1 text-slate-500 dark:text-slate-400">
              Note why this amount cannot be recovered — for example, the family could not be traced. This stays on the permanent record with your name.
            </p>
          </div>

          {agingDays != null && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="writeoff-dues-discipline-note">
              {`Most dues are written off only after 90 days of follow-up. This due is ${agingDays} days old.`}
            </p>
          )}

          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12px] p-2.5" data-testid="writeoff-dues-terminal-warning">
            A write-off cannot be reversed. If the family pays later, that payment is recorded separately by an admin — this due will not re-open.
          </div>

          {!fieldState.valid && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300" data-testid="writeoff-dues-field-helper">{fieldState.helper}</p>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose} data-testid="writeoff-dues-cancel">Cancel</button>
          <button
            type="button"
            className={btnPrimary}
            disabled={!fieldState.valid || busy}
            title={!fieldState.valid ? fieldState.helper : undefined}
            onClick={requestConfirm}
            data-testid="writeoff-dues-confirm"
          >
            {busy ? 'Writing off…' : `Write off ${formatPaise(effectiveDuesPaise)} as uncollectable`}
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmOpen}
        icon={null}
        variant="danger"
        onConfirm={submit}
        onCancel={() => setConfirmOpen(false)}
        {...writeOffConfirmCopy({ amountPaise: effectiveDuesPaise, patientName })}
      />
    </div>
  );
}
